import {
  B2BBarcodeContextState,
  BarcodesOfOrders,
  GetBarcodesForExternalCheckoutResponse,
  OrderVerifyStatus,
} from '../types/B2BBarcode';
import {B2BBarcodesContext, initialState} from '../context/B2BBarcode';
import {Dispatch, useContext, useReducer, useState} from 'react';
import {PayloadAction, createSlice} from '@reduxjs/toolkit';

import {APIV1Version} from '@buncha/common/types/common';
import {GET_BARCODES_FOR_EXTERNAL_CHECKOUT} from '@buncha/config/graphQL/documentNode/queries';
import {ToastType} from '@buncha/components/composites/notification/type';
import {UPDATE_B2B_ORDER_VERIFY_STATUS} from '@buncha/config/graphQL/documentNode/mutation';
import {gqlService} from '@buncha/config/graphQL';
import {useToastMessage} from '@buncha/components/composites/notification';

const reducer = {
  setBarcodesData: function (
    state: B2BBarcodeContextState,
    action: PayloadAction<BarcodesOfOrders[]>,
  ) {
    state.listOfBarcodes = action.payload;
  },
  setBarcodesScannedCount: function (
    state: B2BBarcodeContextState,
    action: PayloadAction<number>,
  ) {
    state.barcodesScannedCount = action.payload;
  },
};

const slice = createSlice({
  initialState,
  name: 'B2BBarcodes',
  reducers: reducer,
});

export function useB2BBarcodesReducer() {
  const B2BBarcodesReducer = useReducer(slice.reducer, initialState);
  return B2BBarcodesReducer;
}

export function useB2BBarcodesContext() {
  return useContext(B2BBarcodesContext);
}

export const B2BBarcodeActions = slice.actions;

export function useGetBarcodesForExternalCheckout(
  version: APIV1Version,
  dispatch: Dispatch<any> | null,
) {
  const [loading, setLoading] = useState(true);
  const [showErrorToast] = useToastMessage(ToastType.Error);
  const [barcodeData, setBarcodeData] = useState<BarcodesOfOrders[]>([]);
  const [barcodeScannedCount, setBarcodeScannedCount] = useState<number>(0);

  const getBarcodesForExternalCheckout = async (
    userId: number,
    runId: number,
    orderLegacyIds?: number[],
  ) => {
    setLoading(true);
    try {
      const response =
        await gqlService?.query<GetBarcodesForExternalCheckoutResponse>({
          query: GET_BARCODES_FOR_EXTERNAL_CHECKOUT,
          variables: {
            version: version,
            requestParams: {
              timeslotId: `${runId}`,
              userId: `${userId}`,
              orderLegacyIds,
            },
          },
          fetchPolicy: 'network-only',
          context: {
            headers: {
              'graphql-version': 'v1',
            },
          },
        });
      const data = response?.data?.getBarcodesForExternalCheckout?.data.V100;
      if (dispatch) {
        dispatch(
          B2BBarcodeActions.setBarcodesData(data?.barcodesOfOrders || []),
        );
        dispatch(
          B2BBarcodeActions.setBarcodesScannedCount(
            data?.barcodeScannedCount || 0,
          ),
        );
      }

      setBarcodeData(data?.barcodesOfOrders || []);
      setBarcodeScannedCount(data?.barcodeScannedCount ?? 0);
      setLoading(false);
    } catch (err: any) {
      setLoading(false);
      showErrorToast(err?.message);
    }
  };

  return {
    getBarcodesForExternalCheckout,
    loading,
    barcodeData,
    barcodeScannedCount,
  };
}

export function useUpdateB2BOrderVerifyStatus(version: APIV1Version) {
  const [loading, setLoading] = useState(false);
  const [showErrorToast] = useToastMessage(ToastType.Error);
  const {state, dispatch} = useB2BBarcodesContext();

  const updateB2BOrderVerifyStatus = async (
    legacyId: number,
    verifyStatus: OrderVerifyStatus,
  ) => {
    setLoading(true);
    try {
      const response = await gqlService?.mutation({
        mutation: UPDATE_B2B_ORDER_VERIFY_STATUS,
        fetchPolicy: 'network-only',
        context: {
          headers: {
            'graphql-version': 'v1',
          },
        },
        variables: {
          version: version,
          requestParams: {
            legacyId: legacyId,
            verifyStatus: verifyStatus,
          },
        },
      });
      const statusUpdate =
        response?.data?.updateB2BOrderVerifyStatus.data.V100.status;
      if (statusUpdate) {
        let newArr = state.listOfBarcodes.map(barcode =>
          barcode.legacyOrderId === legacyId
            ? {...barcode, isBarcodeScanned: true}
            : barcode,
        );
        if (dispatch) {
          dispatch(B2BBarcodeActions.setBarcodesData(newArr));
          dispatch(
            B2BBarcodeActions.setBarcodesScannedCount(
              state.barcodesScannedCount + 1,
            ),
          );
        }
      }

      setLoading(false);
    } catch (err: any) {
      setLoading(false);
      showErrorToast(err?.message);
    }
  };

  return {
    updateB2BOrderVerifyStatus,
    loading,
  };
}
